import { useEffect, useRef } from 'react';
import { message } from '../utils/antdMessage';
import siteConfig from '../config/site-config.js';

/**
 * 救援推送页 WebSocket
 */
export default function useRescuePushRealtime({
  setTargetForm,
  pickPointTokens,
  setPickPointTokens,
  trackerTokens,
  handleTrackReceivedRef,
  handleTargetReceivedRef,
  clearAllTracksRef,
  clearAllTargetsRef,
  fetchPickPointTokensRef,
  fetchTrackerTokensRef
}) {
  const wsRef = useRef(null);
  const pickPointTokensRef = useRef([]);
  const trackerTokensRef = useRef([]);

  useEffect(() => {
    pickPointTokensRef.current = pickPointTokens;
  }, [pickPointTokens]);

  useEffect(() => {
    trackerTokensRef.current = trackerTokens;
  }, [trackerTokens]);

  useEffect(() => {
    const wsUrl = siteConfig.getWsUrl();
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('救援推送页面WebSocket连接已建立');
      // 令牌绑定等敏感事件只推给已认证连接（与指挥页 dashboardWsHandlers 相同的 auth 方式）
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
        if (token) {
          ws.send(JSON.stringify({ type: 'auth', token }));
        }
      } catch (_e) {
        /* ignore */
      }
    };

    ws.onmessage = (event) => {
      try {
        const wsMsg = JSON.parse(event.data);
        console.log('收到WebSocket消息:', wsMsg);

        if (wsMsg.type === 'rescue_track') {
          console.log('收到轨迹推送:', wsMsg.data);
          handleTrackReceivedRef.current(wsMsg.data);
        } else if (wsMsg.type === 'rescue_target') {
          console.log('收到救援目标推送:', wsMsg.data);
          handleTargetReceivedRef.current(wsMsg.data);
        } else if (wsMsg.type === 'pick_point_progress') {
          const point = wsMsg.payload || wsMsg.data;
          if (point?.token && typeof setPickPointTokens === 'function') {
            setPickPointTokens((prev) => {
              const list = prev || [];
              const index = list.findIndex((item) => item.token === point.token);
              if (index === -1) return list;
              const next = [...list];
              next[index] = { ...next[index], ...point };
              return next;
            });
          }
        } else if (wsMsg.type === 'pick_point_amended') {
          const point = wsMsg.payload || wsMsg.data;
          if (point?.token && typeof setPickPointTokens === 'function') {
            setPickPointTokens((prev) => {
              const list = prev || [];
              const index = list.findIndex((item) => item.token === point.token);
              if (index === -1) return list;
              const next = [...list];
              next[index] = { ...next[index], ...point };
              return next;
            });
          }
          if (point?.token && pickPointTokensRef.current.some((item) => item.token === point.token)
            && Number(point.manual_lat) && Number(point.manual_lng)
            && !(Number(point.manual_lat) === 0 && Number(point.manual_lng) === 0)) {
            setTargetForm((form) => ({
              ...form,
              lat: Number(point.manual_lat).toFixed(6),
              lng: Number(point.manual_lng).toFixed(6),
              map_link: ''
            }));
          }
        } else if (wsMsg.type === 'pick_point_submitted') {
          const point = wsMsg.payload || wsMsg.data;
          if (point?.token && pickPointTokensRef.current.some((item) => item.token === point.token)) {
            const usedLat = Number(point.used_lat);
            const usedLng = Number(point.used_lng);
            if (Number.isFinite(usedLat) && Number.isFinite(usedLng) && !(usedLat === 0 && usedLng === 0)) {
              setTargetForm((form) => ({
                ...form,
                lat: usedLat.toFixed(6),
                lng: usedLng.toFixed(6),
                map_link: ''
              }));
              message.success('手机选点已收到，已填入目标坐标');
            }
            if (typeof setPickPointTokens === 'function') {
              setPickPointTokens((prev) => {
                const list = prev || [];
                const index = list.findIndex((item) => item.token === point.token);
                if (index === -1) return [point, ...list];
                const next = [...list];
                next[index] = { ...next[index], ...point };
                return next;
              });
            } else {
              fetchPickPointTokensRef.current();
            }
          }
        } else if (wsMsg.type === 'tracker_token_bound') {
          const bound = wsMsg.payload || wsMsg.data || {};
          const known = !bound.token || trackerTokensRef.current.some((item) => item.token === bound.token);
          if (known) {
            const who = [bound.name, bound.group_name].filter(Boolean).join(' · ') || 'App 队员';
            message.success(`令牌已被绑定：${who}`);
            fetchTrackerTokensRef.current();
          }
        }
      } catch (error) {
        console.error('WebSocket消息解析失败:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket连接错误:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket连接已关闭');
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      try {
        clearAllTracksRef.current();
        clearAllTargetsRef.current();
      } catch (error) {
        console.log('组件卸载清理时出错:', error);
      }
    };
  }, []);

  return { wsRef };
}
